// Dependency-free builders for the SVG pattern generator, the ASCII-art banner
// generator, and the shields.io-style badge generator. All output is generated
// locally; nothing here touches the network.

/* -------------------------------------------------------------------------- */
/* Shared helpers                                                             */
/* -------------------------------------------------------------------------- */

export function sanitizeColor(input: string, fallback: string): string {
  const value = input.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) {
    return value;
  }
  if (/^(rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\)$/.test(value)) {
    return value;
  }
  if (/^[a-zA-Z]{3,20}$/.test(value)) {
    return value.toLowerCase();
  }
  return fallback;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Encode an SVG string as a compact, CSS-friendly data URI. */
export function svgToDataUri(svg: string): string {
  const encoded = svg
    .replace(/\n/g, '')
    .replace(/%/g, '%25')
    .replace(/#/g, '%23')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E')
    .replace(/"/g, "'")
    .replace(/&/g, '%26')
    .replace(/\s+/g, ' ');
  return `data:image/svg+xml,${encoded}`;
}

/* -------------------------------------------------------------------------- */
/* SVG pattern generator                                                      */
/* -------------------------------------------------------------------------- */

export type SvgPatternKind = 'dots' | 'grid' | 'lines' | 'cross' | 'zigzag' | 'triangles';

export type SvgPatternOptions = {
  kind: SvgPatternKind;
  background: string;
  foreground: string;
  tile: number;
  scale: number;
  strokeWidth: number;
};

export const SVG_PATTERN_KINDS: Array<{ value: SvgPatternKind; label: string }> = [
  { value: 'dots', label: 'Dots' },
  { value: 'grid', label: 'Grid' },
  { value: 'lines', label: 'Diagonal lines' },
  { value: 'cross', label: 'Cross / plus' },
  { value: 'zigzag', label: 'Zigzag' },
  { value: 'triangles', label: 'Triangles' },
];

export const DEFAULT_SVG_PATTERN: SvgPatternOptions = {
  kind: 'dots',
  background: '#0f172a',
  foreground: '#38bdf8',
  tile: 24,
  scale: 3,
  strokeWidth: 2,
};

function patternShapes(options: SvgPatternOptions): string {
  const t = options.tile;
  const fg = options.foreground;
  const sw = options.strokeWidth;
  const r = Math.max(0.5, (options.scale / 100) * t);

  switch (options.kind) {
    case 'dots':
      return `<circle cx="${t / 2}" cy="${t / 2}" r="${r}" fill="${fg}"/>`;
    case 'grid':
      return `<path d="M${t} 0H0V${t}" fill="none" stroke="${fg}" stroke-width="${sw}"/>`;
    case 'lines':
      return (
        `<path d="M0 ${t}L${t} 0M-1 1L1 -1M${t - 1} ${t + 1}L${t + 1} ${t - 1}" ` +
        `stroke="${fg}" stroke-width="${sw}" fill="none"/>`
      );
    case 'cross': {
      const c = t / 2;
      const arm = Math.max(2, r + 2);
      return (
        `<path d="M${c} ${c - arm}V${c + arm}M${c - arm} ${c}H${c + arm}" ` +
        `stroke="${fg}" stroke-width="${sw}" fill="none"/>`
      );
    }
    case 'zigzag':
      return (
        `<path d="M0 ${t * 0.75}L${t / 2} ${t * 0.25}L${t} ${t * 0.75}" ` +
        `fill="none" stroke="${fg}" stroke-width="${sw}"/>`
      );
    case 'triangles':
      return `<path d="M${t / 2} ${t * 0.2}L${t * 0.85} ${t * 0.8}H${t * 0.15}Z" fill="${fg}"/>`;
    default:
      return '';
  }
}

export function buildSvgPattern(options: SvgPatternOptions): string {
  const t = Math.max(4, Math.round(options.tile));
  const bg = options.background;
  const shapes = patternShapes({ ...options, tile: t });
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${t}" height="${t}" viewBox="0 0 ${t} ${t}">`,
    `<rect width="${t}" height="${t}" fill="${bg}"/>`,
    shapes,
    `</svg>`,
  ].join('');
}

export function buildSvgPatternPreview(options: SvgPatternOptions, tiles = 6): string {
  const t = Math.max(4, Math.round(options.tile));
  const size = t * tiles;
  const shapes = patternShapes({ ...options, tile: t });
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" `,
    `viewBox="0 0 ${size} ${size}" preserveAspectRatio="xMidYMid slice">`,
    `<defs><pattern id="cleanorpat" width="${t}" height="${t}" patternUnits="userSpaceOnUse">`,
    `<rect width="${t}" height="${t}" fill="${options.background}"/>`,
    shapes,
    `</pattern></defs>`,
    `<rect width="${size}" height="${size}" fill="url(#cleanorpat)"/>`,
    `</svg>`,
  ].join('');
}

export function buildPatternCssBackground(options: SvgPatternOptions): string {
  const uri = svgToDataUri(buildSvgPattern(options));
  const t = Math.max(4, Math.round(options.tile));
  return `background-color: ${options.background};\nbackground-image: url("${uri}");\nbackground-size: ${t}px ${t}px;`;
}

/* -------------------------------------------------------------------------- */
/* Text to ASCII art (compact block font)                                     */
/* -------------------------------------------------------------------------- */

// A compact 5-row block font. Each glyph is 5 rows tall; columns vary. Rows use
// '#' for a filled cell and ' ' for empty. Characters are joined with a spacer.
const ASCII_FONT_HEIGHT = 5;

const ASCII_FONT: Record<string, string[]> = {
  A: [' ## ', '#  #', '####', '#  #', '#  #'],
  B: ['### ', '#  #', '### ', '#  #', '### '],
  C: [' ###', '#   ', '#   ', '#   ', ' ###'],
  D: ['### ', '#  #', '#  #', '#  #', '### '],
  E: ['####', '#   ', '### ', '#   ', '####'],
  F: ['####', '#   ', '### ', '#   ', '#   '],
  G: [' ###', '#   ', '# ##', '#  #', ' ###'],
  H: ['#  #', '#  #', '####', '#  #', '#  #'],
  I: ['###', ' # ', ' # ', ' # ', '###'],
  J: ['  ##', '   #', '   #', '#  #', ' ## '],
  K: ['#  #', '# # ', '##  ', '# # ', '#  #'],
  L: ['#   ', '#   ', '#   ', '#   ', '####'],
  M: ['#   #', '## ##', '# # #', '#   #', '#   #'],
  N: ['#  #', '## #', '# ##', '#  #', '#  #'],
  O: [' ## ', '#  #', '#  #', '#  #', ' ## '],
  P: ['### ', '#  #', '### ', '#   ', '#   '],
  Q: [' ## ', '#  #', '#  #', '# ##', ' ###'],
  R: ['### ', '#  #', '### ', '# # ', '#  #'],
  S: [' ###', '#   ', ' ## ', '   #', '### '],
  T: ['#####', '  #  ', '  #  ', '  #  ', '  #  '],
  U: ['#  #', '#  #', '#  #', '#  #', ' ## '],
  V: ['#   #', '#   #', '#   #', ' # # ', '  #  '],
  W: ['#   #', '#   #', '# # #', '## ##', '#   #'],
  X: ['#  #', ' ## ', ' ## ', '#  #', '#  #'],
  Y: ['#   #', ' # # ', '  #  ', '  #  ', '  #  '],
  Z: ['####', '  # ', ' #  ', '#   ', '####'],
  '0': [' ## ', '#  #', '#  #', '#  #', ' ## '],
  '1': [' # ', '## ', ' # ', ' # ', '###'],
  '2': [' ## ', '#  #', '  # ', ' #  ', '####'],
  '3': ['### ', '   #', ' ## ', '   #', '### '],
  '4': ['#  #', '#  #', '####', '   #', '   #'],
  '5': ['####', '#   ', '### ', '   #', '### '],
  '6': [' ###', '#   ', '### ', '#  #', ' ## '],
  '7': ['####', '   #', '  # ', ' #  ', ' #  '],
  '8': [' ## ', '#  #', ' ## ', '#  #', ' ## '],
  '9': [' ## ', '#  #', ' ###', '   #', '### '],
  '!': ['#', '#', '#', ' ', '#'],
  '?': ['### ', '   #', ' ## ', '    ', ' #  '],
  '.': ['  ', '  ', '  ', '  ', '# '],
  ',': ['  ', '  ', '  ', ' #', '# '],
  ':': ['  ', '# ', '  ', '# ', '  '],
  '-': ['    ', '    ', '####', '    ', '    '],
  '+': ['   ', ' # ', '###', ' # ', '   '],
  '=': ['    ', '####', '    ', '####', '    '],
  '*': ['     ', '# # #', ' ### ', '# # #', '     '],
  '/': ['   #', '  # ', ' #  ', '#   ', '#   '],
  '@': [' ## ', '# ##', '# ##', '#   ', ' ## '],
  '#': [' # # ', '#####', ' # # ', '#####', ' # # '],
  $: [' ###', '# # ', ' ## ', ' # #', '### '],
  '(': [' #', '# ', '# ', '# ', ' #'],
  ')': ['# ', ' #', ' #', ' #', '# '],
  "'": ['#', '#', ' ', ' ', ' '],
  ' ': ['  ', '  ', '  ', '  ', '  '],
};

export function textToAsciiArt(input: string, letterSpacing = 1): string {
  const chars = Array.from(input.toUpperCase());
  const rows: string[] = new Array<string>(ASCII_FONT_HEIGHT).fill('');
  const spacer = ' '.repeat(Math.max(0, letterSpacing));

  const glyphs = chars.map((ch) => ASCII_FONT[ch] ?? ASCII_FONT[' ']);
  glyphs.forEach((glyph, index) => {
    for (let r = 0; r < ASCII_FONT_HEIGHT; r++) {
      rows[r] += glyph[r];
      if (index < glyphs.length - 1) {
        rows[r] += spacer;
      }
    }
  });

  return rows.join('\n');
}

/* -------------------------------------------------------------------------- */
/* GitHub / shields.io-style badge generator                                  */
/* -------------------------------------------------------------------------- */

export type BadgeOptions = {
  label: string;
  message: string;
  labelColor: string;
  color: string;
  logoText: string;
};

export const BADGE_COLOR_PRESETS: Array<{ value: string; label: string }> = [
  { value: '#4c1', label: 'Green' },
  { value: '#007ec6', label: 'Blue' },
  { value: '#dfb317', label: 'Yellow' },
  { value: '#e05d44', label: 'Red' },
  { value: '#fe7d37', label: 'Orange' },
  { value: '#9f9f9f', label: 'Grey' },
  { value: '#4c1', label: 'Brightgreen' },
  { value: '#555', label: 'Dark' },
];

export const DEFAULT_BADGE: BadgeOptions = {
  label: 'build',
  message: 'passing',
  labelColor: '#555',
  color: '#4c1',
  logoText: '',
};

// Approximate per-character advance widths (in 1/10 px at 11px Verdana-like
// font), good enough for tight, shields-style label sizing.
const CHAR_WIDTHS: Record<string, number> = {
  ' ': 40,
  i: 30,
  j: 30,
  l: 30,
  I: 40,
  f: 40,
  t: 40,
  r: 45,
  '.': 30,
  ',': 30,
  ':': 30,
  ';': 30,
  '!': 35,
  m: 100,
  w: 95,
  W: 100,
  M: 100,
};

function estimateTextWidth(text: string): number {
  let total = 0;
  for (const ch of text) {
    if (ch in CHAR_WIDTHS) {
      total += CHAR_WIDTHS[ch];
    } else if (ch >= 'A' && ch <= 'Z') {
      total += 75;
    } else if (ch >= '0' && ch <= '9') {
      total += 70;
    } else {
      total += 65;
    }
  }
  return total / 10; // convert to px
}

export function buildBadgeSvg(options: BadgeOptions): string {
  const height = 20;
  const paddingX = 6;
  const labelText = options.label;
  const messageText = options.message;
  const logo = options.logoText.trim();

  const logoWidth = logo ? estimateTextWidth(logo) + 6 : 0;
  const labelTextWidth = estimateTextWidth(labelText);
  const messageTextWidth = estimateTextWidth(messageText);

  const labelWidth = Math.round(labelTextWidth + paddingX * 2 + logoWidth);
  const messageWidth = Math.round(messageTextWidth + paddingX * 2);
  const totalWidth = labelWidth + messageWidth;

  const labelColor = options.labelColor;
  const color = options.color;
  const labelCenter = (labelWidth / 2) * 10;
  const messageCenter = (labelWidth + messageWidth / 2) * 10;
  const roundedLabel = escapeXml(labelText);
  const roundedMessage = escapeXml(messageText);
  const logoLabel = logo ? escapeXml(logo) : '';

  const logoTspan = logo
    ? `<text x="${paddingX * 10}" y="140" transform="scale(.1)" fill="#fff" font-weight="bold" ` +
      `textLength="${logoWidth * 10 - 40}">${logoLabel}</text>`
    : '';

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${height}" `,
    `role="img" aria-label="${roundedLabel}: ${roundedMessage}">`,
    `<title>${roundedLabel}: ${roundedMessage}</title>`,
    `<linearGradient id="s" x2="0" y2="100%">`,
    `<stop offset="0" stop-color="#bbb" stop-opacity=".1"/>`,
    `<stop offset="1" stop-opacity=".1"/></linearGradient>`,
    `<clipPath id="r"><rect width="${totalWidth}" height="${height}" rx="3" fill="#fff"/></clipPath>`,
    `<g clip-path="url(#r)">`,
    `<rect width="${labelWidth}" height="${height}" fill="${labelColor}"/>`,
    `<rect x="${labelWidth}" width="${messageWidth}" height="${height}" fill="${color}"/>`,
    `<rect width="${totalWidth}" height="${height}" fill="url(#s)"/>`,
    `</g>`,
    `<g fill="#fff" text-anchor="middle" font-family="Verdana,Geneva,DejaVu Sans,sans-serif" `,
    `font-size="110" text-rendering="geometricPrecision">`,
    logoTspan,
    `<text x="${labelCenter + logoWidth * 5}" y="150" fill="#010101" fill-opacity=".3" `,
    `transform="scale(.1)" textLength="${labelTextWidth * 10}">${roundedLabel}</text>`,
    `<text x="${labelCenter + logoWidth * 5}" y="140" transform="scale(.1)" `,
    `textLength="${labelTextWidth * 10}">${roundedLabel}</text>`,
    `<text x="${messageCenter}" y="150" fill="#010101" fill-opacity=".3" `,
    `transform="scale(.1)" textLength="${messageTextWidth * 10}">${roundedMessage}</text>`,
    `<text x="${messageCenter}" y="140" transform="scale(.1)" `,
    `textLength="${messageTextWidth * 10}">${roundedMessage}</text>`,
    `</g></svg>`,
  ].join('');
}

export function buildBadgeMarkdown(options: BadgeOptions, altBase = 'badge'): string {
  const alt = `${options.label}: ${options.message}`.trim() || altBase;
  return `![${alt}](badge.svg)`;
}

export function buildBadgeHtml(options: BadgeOptions): string {
  const alt = escapeXml(`${options.label}: ${options.message}`.trim());
  return `<img src="badge.svg" alt="${alt}" />`;
}
